import React, { useEffect, useState } from 'react';
import { X, History, Loader2, AlertCircle } from 'lucide-react';
import { getSessionAudit, SmartStartAuditEvent } from '../../services/smartStartApi';

interface Props {
  open: boolean;
  sessionId: number | null;
  onClose: () => void;
}

const kindColors: Record<string, string> = {
  session_created: 'bg-gray-100 text-gray-700',
  classified: 'bg-lavender/10 text-lavender',
  pipeline_selected: 'bg-lavender/10 text-lavender',
  stage_started: 'bg-amber-100 text-amber-700',
  stage_completed: 'bg-emerald-100 text-emerald-700',
  stage_awaiting_review: 'bg-amber-100 text-amber-700',
  stage_awaiting_intake: 'bg-amber-100 text-amber-700',
  stage_approved: 'bg-emerald-100 text-emerald-700',
  stage_rejected: 'bg-red-100 text-red-700',
  stage_failed: 'bg-red-100 text-red-700',
  stage_skipped: 'bg-gray-100 text-gray-700',
  stage_reviewed: 'bg-purple-100 text-purple-700',
  stage_error: 'bg-red-100 text-red-700',
  orchestrator_plan_generated: 'bg-lavender/10 text-lavender',
  orchestrator_step_opened: 'bg-blue-100 text-blue-700',
  orchestrator_step_completed: 'bg-emerald-100 text-emerald-700',
  orchestrator_step_rejected: 'bg-red-100 text-red-700',
  orchestrator_step_abandoned: 'bg-amber-100 text-amber-700',
  intake_slot_filled: 'bg-lavender/10 text-lavender',
  intake_slot_assumed: 'bg-amber-100 text-amber-800',
  consultant_chat_message: 'bg-blue-100 text-blue-700',
};

const AuditDrawer: React.FC<Props> = ({ open, sessionId, onClose }) => {
  const [events, setEvents] = useState<SmartStartAuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const list = await getSessionAudit(sessionId);
        if (!cancelled) setEvents(list);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || err.message || String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sessionId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative ml-auto w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-lavender" />
            <h2 className="text-lg font-bold text-gray-900">Audit trail</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {loading && (
          <div className="p-6 flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {error && (
          <div className="p-6">
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div className="p-6 text-sm text-gray-500">No audit events yet.</div>
        )}

        {!loading && !error && events.length > 0 && (
          <ol className="p-4 space-y-2">
            {events.map((e, i) => {
              const kind = typeof e.kind === 'string' ? e.kind : 'event';
              const parsedTs = e.ts ? new Date(e.ts) : null;
              const tsLabel = parsedTs && !Number.isNaN(parsedTs.getTime())
                ? parsedTs.toLocaleString()
                : '—';
              return (
                <li
                  key={i}
                  className="p-3 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wide ${
                        kindColors[kind] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {kind.replace(/_/g, ' ')}
                    </span>
                    {typeof e.stage_index === 'number' && (
                      <span className="text-xs text-gray-500">
                        Stage {e.stage_index + 1}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-gray-400">{tsLabel}</span>
                  </div>
                  <div className="text-sm text-gray-800">{e.detail || ''}</div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
};

export default AuditDrawer;
